import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DPForm } from '../models/DPForm';
import { DPFormResult } from '../models/DPFormResult';
import { DPJob } from '../models/DPJob';
import { ApiService } from './api.service';
import { FormsServiceInterface } from './forms.service.interface';

/**
 * Form management service.
 */
@Injectable({
  providedIn: 'root',
})
export class FormsService implements FormsServiceInterface {
  private _taskForm: DPForm;

  constructor(private api: ApiService) {}

  /**
   * Get all forms for a job
   *
   * @param jobID Job ID
   * @param force If true, it forces retrieving from backend.
   */
  public getFormsForJob(jobID: number, force?: boolean) {
    return this.api.getFormList(jobID, {
      force,
    });
  }

  /**
   * Get form data of a sepcific user form.
   * The form data contains the form fields grouped in pages along with user answers.
   *
   * @param userFormID User form ID.
   * @param force If true, it forces retrieving from backend.
   * @returns
   */
  public getFormData(userFormID: number, force?: boolean) {
    return this.api.getElectronicForm(userFormID, {
      alias: [`Form${userFormID}`, 'FormData'],
      force,
    });
  }

  /**
   * Get form pdf data in base64 format.
   *
   * @param userFormID User form ID.
   * @returns {Object}
   *   pdfForm: PDF form data in base64 format.
   */
  public getFormPDF(userFormID: number) {
    return this.api.getFormPDF(userFormID, {
      alias: [`PDF${userFormID}`, 'FormPDF'],
    });
  }

  /**
   * Send request to sign PDF form.
   *
   * @param userFormID User form ID.
   * @returns
   */
  public signFormPDF(userFormID: number) {
    return this.api.signForm(userFormID);
  }

  /**
   * Save form answers.
   *
   * @param userFormID User form ID.
   * @param result Form values object.
   * @returns
   */
  public saveFormAnswers(userFormID: number, result: DPFormResult) {
    return this.api.saveFormResult(userFormID, result);
  }

  /**
   * This API will create user form on the backend to store all user answers.
   * The user form will be used to get answers, create PDF form, etc.
   *
   * @param formID Form ID retrieved in getFormData
   * @param job Job object which has jobID
   * @returns
   */
  public startFormForJob(formID: number, job: DPJob) {
    return this.getForm(formID, job.jobID).pipe(
      switchMap((form: DPForm) =>
        this.api.startFormForJob(form.formID, job.userJobID)
      ),
      switchMap(() => this.getFormsForJob(job.jobID, true)),
      map((forms: DPForm[]) => forms.find((f) => f.formID === formID))
    );
  }

  /**
   * This API will create user form for tasks in Dashboard.
   *
   * @param formID Form ID retrieved in getComplianceInfo in Dashboard service.
   * @param taskID Task ID. Also retrieved in getComplianceInfo in Dashboard service.
   * @returns
   */
  public startFormForTask(formID: number, taskID: number) {
    return this.api.startFormForTask(formID, taskID);
  }

  /**
   * Get form object among job forms or task forms.
   *
   * @param formID Form ID
   * @param jobID Job ID
   * @returns
   */
  public getForm(formID: number, jobID: number): Observable<DPForm> {
    if (this._taskForm && !jobID) {
      return this._taskForm.formID === formID ? of(this._taskForm) : of(null);
    }

    return this.getFormsForJob(jobID).pipe(
      map((jobForms: DPForm[]) => {
        const form = jobForms.find((f) => f.formID === formID);
        return form;
      })
    );
  }

  public reset() {
    this._taskForm = null;
  }
}
